import * as React from 'react';
import { cn } from '@/lib/utils';

type InputProps = React.ComponentProps<'input'> &
  Readonly<{
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    wrapperClassName?: string;
  }>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, startAdornment, endAdornment, wrapperClassName, ...props }, ref) => {
    const inputElement = (
      <input
        type={type}
        data-slot="input"
        className={cn(
          'h-10 w-full min-w-0 rounded-lg border bg-foreground px-3 py-2 text-base text-card-foreground transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-card-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
          startAdornment && 'pl-9',
          endAdornment && 'pr-9',
          className
        )}
        ref={ref}
        {...props}
      />
    );

    if (!startAdornment && !endAdornment) {
      return inputElement;
    }

    return (
      <div className={cn('relative', wrapperClassName)}>
        {startAdornment && (
          <div className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
            {startAdornment}
          </div>
        )}
        {inputElement}
        {endAdornment && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
            {endAdornment}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
export type { InputProps };
